<template>
  <div class="url">
    <div v-if="isDataUpdating" class="page-cover-loader">
      <base-loader />
    </div>
    <div class="container" v-if="snapshotsMissing">
      <div class="rank-zone__no-data">Looks like you don’t have any ranking data to report.</div>
    </div>
    <div class="container" v-else-if="!isDataFetched">
      <div class="rank-zone__loader">
        <base-loader />
      </div>
    </div>
    <div class="container" v-else>

      <div class="table-wrapper">
        <div class="table-wrapper__header url__table-controls">
          <div class="url__radio-wrap">
            <base-radio class="url__radio"
                        label="Mapped"
                        v-model="filters.type"
                        value="1" />
            <base-radio class="url__radio"
                        label="Ranked"
                        v-model="filters.type"
                        value="2" />

            <base-select class="url__web-property-select"
                         v-if="filters.type === '2'"
                         :options="webPropertyGroups"
                         v-model="filters.webPropertyGroupId"
                         showTopBorder
                         truncateLabel
                         placeholder="Web Property">
              <template #type="{option}">
                <span v-if="option.type === 'Clients'" class="url__client"/>
                <span v-else-if="option.type === 'Siblings'" class="url__sibling"/>
                <span v-else-if="option.type === 'Competitors'" class="url__competitor"/>
              </template>
            </base-select>

          </div>
          <div class="url__select-wrap">
            <div class="url__select-holder url__select-holder_main">
              <div class="url__pagination-props-select">
                <span class="url__count">URLs: {{urls.length}}</span>
                <base-select
                  class="url__select"
                  :options="paginationOptions"
                  alignLeft
                  :titleStyle="{'padding-left': '5px'}"
                  :headerStyle="{}"
                  showTopBorder
                  shiftLabelRight
                  v-model="pageItemsLimit"
                  :selectStyle="{width: '100px !important'}"
                  @change="handleLimitChange"
                />
              </div>
              <base-select class="url__select"
                           v-model="filters.snapshot_date"
                           :showTopBorder="true"
                           :options="formattedSnapshots"
                           @change="onSnapshotChanged()"/>
            </div>
          </div>
        </div>
        <div class="table-wrapper__body">
          <base-table-collapsible class="url__table"
                                  truncateUrl
                                  :config="config"
                                  :data="tableData"
                                  :count="urls.length"
                                  :filteredSum="filteredSum"
                                  :paginatedTable="true"
                                  :showLinkToCB="filters.type == 1"
                                  :show-calendar="filters.type == 1"
                                  @sort="onSortChanged"
                                  ref="table"/>
        </div>
        <base-pagination class="url__pagination"
                         :totalItems="urls.length"
                         :limit="pageLengthLimit"
                         @pageChanged="setPaginationProps"
                         v-if="urls.length > pageLengthLimit">
        </base-pagination>
      </div>
    </div>
    <modal name="url"
           v-if="selectedClient"
           v-bind="urlModalProps">
      <UrlPopup :title="selectedUrl.label"
                :client="selectedClient._id"
                :url="selectedUrl._id"
                :date="filters.snapshot_date"
                :selectedAdvancedOptions="selectedAdvancedOptions"
                @close="hidePopup" />
    </modal>
  </div>
</template>

<script>
import _ from 'lodash'
import baseModalProps from '@/common/BaseModalProps'
import { mapActions, mapState } from 'vuex'
import filters from '@/common/Filters'
import { convertAdvancedOptionsToFilters } from '@/features/reports/common/advanced-options/config'
import { baseComponent, thirdLevelTableConfig, fourthLevelTableConfig } from '@/features/reports/common/Mixins'
import helper from '@/services/Helper'

const ITEMS_PER_PAGE = 100
/**
 * URL page.
 * Used in RankZone
 */
export default {
  name: 'Url',
  mixins: [baseComponent, thirdLevelTableConfig, fourthLevelTableConfig],
  components: {
    // to avoid circular dependencies https://vuejs.org/v2/guide/components-edge-cases.html#Circular-References-Between-Components
    UrlPopup: () => import('@/features/reports/common/UrlPopup')
  },
  created () {
    this.prepareDataProperties()
  },
  props: {
    /**
     * Advanced options from parent element
     */
    selectedAdvancedOptions: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      // objects that contains current pagination status
      paginationProps: { from: 0, to: ITEMS_PER_PAGE },
      // url that should be shown in UrlPopup
      selectedUrl: {},
      sortConfig: [],
      // additional filters applied to page
      // contains snapshot data and radiobutton type (Mapped/Ranked)
      filters: {
        snapshot_date: '',
        type: '1',
        webPropertyGroupId: null
      },
      // snapshot list for the snapshot dropdown
      formattedSnapshots: [],
      paginationOptions: [
        {
          label: '100',
          value: 100
        },
        {
          label: '250',
          value: 250
        },
        {
          label: '500',
          value: 500
        }
      ],
      pageItemsLimit: ITEMS_PER_PAGE,
    }
  },
  computed: {
    ...mapState('rankZoneCommon', {
      /**
       * @alias expanedUrl
       * */
      expandedItem: 'expandedItem',
      /**
       * path to constant object holds paths to corresponding page specific store actions
       * to set received form BE keyword from common store into corresponding store
       * */
      KEYWORDS_PATH: 'KEYWORDS_PATH',
      webPropertyGroups (state) {
        return state.webPropertyGroups.map(item => {
          return {
            value: item._id,
            label: item.label,
            type: item.type,
            attributes: item.type === 'Clients' ? [{ 'private-content': true }] : [],
           }
        }) || []
      }
    }),
    ...mapState('rankZoneUrl', [
      'urls', 'unfiltered_totals'
    ]),
    /**
     * modal props for {@link UrlPopup}
     * */
    urlModalProps: function () {
      const urlModalProps = {
        width: 980
      }
      return { ...baseModalProps, ...urlModalProps }
    },
    /**
     * Table config
     */
    config () {
      return [
        this.thirdLevelConfig,
        this.forthLevelConfig
      ]
    },
    /**
     * table data.
     * Pay attenton: data on this screen is paginated, so sort functionality was moved from {@link BaseTableCollapsible}
     * to Url component
     * @returns array of urls
     */
    tableData () {
      if (!this.sortConfig.length) {
        return this.urls.slice(this.paginationProps.from, this.paginationProps.to)
      }

      /**
       * sorts data in all levels
       * @param data sorted data
       * @param param - property name to sort by
       * @param direction - sort direction
       */
      function sortData (data, param, direction) {
        helper.sortByParamMutation(data, param, direction)
        data.forEach(item => {
          if (item.children && item.children.length) {
            sortData(item.children, param, direction)
          }
        })
      }
      const { propertyName, sortDirection } = this.sortConfig[0]
      const sortedData = _.cloneDeep(this.urls)
      sortData(sortedData, propertyName, sortDirection)
      return sortedData.slice(this.paginationProps.from, this.paginationProps.to)
    },
    /**
     * max length paginated list
     * @returns {number}
     */
    pageLengthLimit () {
      return this.pageItemsLimit
    },
    /**
     * config for filtered sum header
     * @returns {{cells: *}}
     */
    sumConfig () {
      const config = { cells: _.cloneDeep(this.config[0].cells) }
      config.type = 'header'
      return config
    },
    /**
     * @alias _filteredSum
     * creates filtered summary for table header
     * it was moved from {@link BaseTableCollapsible} because of pagination
     */
    filteredSum () {
      const data = {}
      this.sumConfig.cells.forEach(cell => {
        if (cell.propName !== 'label') {
          data[cell.propName] = 0
        }
      })
      this.calculateFilteredSum(this.urls, data)
      data.label = 'Filter summary'
      return data
    }
  },
  watch: {
    selectedClient: {
      handler () {
        this.isDataFetched = false
      },
      immediate: true
    },
    webPropertyGroups: {
      handler (groups) {
        if (groups.length) {
          /* set default webPropertyGroup */
          this.filters.webPropertyGroupId = groups[0].value
        }
      },
      immediate: true
    },
    'filters.type' (v) {
      if (v === '1') {
        this.filters.webPropertyGroupId = this.webPropertyGroups[0].value
      }
      this.updateData()
    },
    'filters.webPropertyGroupId' () {
      this.updateData()
    },
    selectedAdvancedOptions: {
      async handler () {
        this.isDataFetched && await this.applyAdvancedOptions()
      },
      deep: true
    },
     /**
     * trigger for loading data
     */
    snapshots: {
      handler () {
         if (!this.selectedClient || !this.snapshotsPresent) {
          return
        }
        this.mapSnapshots()
        this.$nextTick(this.loadPageData)
      },
      immediate: true

    },
    urls () {
      this.setPaginationProps(this.paginationProps)
    },
    expandedItem (item) {
      if (item) {
        this.getKeywords(item._id)
      }
    }
  },
  methods: {
    ...mapActions('rankZoneUrl', ['getUrlData']),
    ...mapActions('rankZoneCommon', ['getKeywordsByUrlIdData']),
    /**
     * prepare table config
     */
    prepareDataProperties: function () {
      const labels = ['URL', '01 - 03', '04 - 07', '08 - 10', '11 - 25', '26 - 50', '51 - 100', 'NR', 'ND', 'Total', 'KW#']
      this.thirdLevelConfig.cells = this.thirdLevelConfig.cells.map((item, index) => {
        if (index === 0) {
          return {
            ...item,
            label: labels[index],
            subtitleSingle: 'URL found',
            subtitlePlural: 'URLs found'
          }
        }
        return {
          ...item,
          label: labels[index]
        }
      })
    },
    /**
     * changes number of paginated items in teh list
     * */
    handleLimitChange (value) {
      this.pageItemsLimit = value
      this.paginationProps = { ...this.paginationProps, from: 0, to: value }
    },
    /**
     * open {@link UrlPopup}
     */
    openPopup (data) {
      this.selectedUrl = {
        _id: data._id,
        label: data.label
      }
      this.$modal.show('url')
    },
     /**
     * hide {@link UrlPopup}
     */
    hidePopup () {
      this.$modal.hide('url')
      this.selectedUrl = {}
    },
    async loadPageData () {
      const advancedFilters = convertAdvancedOptionsToFilters(this.selectedAdvancedOptions)
      const filters = Object.assign({}, this.filters, { advancedFilters })
      const data = await this.getUrlData({ clientId: this.clientId, filters })
      data && (this.isDataFetched = true)
    },
    /**
     * snapshot dropdown callback
     */
    onSnapshotChanged: async function () {
      this.$refs['table'].collapseTable()
      await this.updateData()
    },
     /**
     * prepare snapshots for dropdown
     */
    mapSnapshots () {
      this.filters.snapshot_date = ''
      this.filters.snapshot_date = this.snapshots[0]
      this.formattedSnapshots = this.snapshots.map(snapshot => {
        return {
          label: this.$options.filters.formattedDate(snapshot),
          value: snapshot
        }
      })
    },
    /**
     * callback for Mapped/Ranked radiobutton group
     * */
    async onTypeChanged () {
      await this.updateData()
    },
    /**
     * change pagination
     * @param props: {from: Number, to: Number}
     */
    setPaginationProps (props) {
      this.paginationProps = props
    },
    async applyAdvancedOptions () {
      this.$refs['table'] && this.$refs['table'].collapseTable()
      await this.updateData()
    },
    /**
     * @alias updateDataCall
     * update data after applying advanced filters or changing snapshot
     */
    async updateData () {
      const advancedFilters = convertAdvancedOptionsToFilters(this.selectedAdvancedOptions)
      const filters = Object.assign({}, this.filters, { advancedFilters })
      if (this.isDataFetched) {
        this.isDataUpdating = true
      }
      await this.getUrlData({ clientId: this.clientId, filters })
      this.isDataFetched = true
      this.isDataUpdating = false
    },
     /**
     * @alias getKeywordsCall
     * @param urlId
     */
    async getKeywords (urlId) {
      const advancedFilters = convertAdvancedOptionsToFilters(this.selectedAdvancedOptions)
      const snapshot_date = this.filters.snapshot_date ? this.filters.snapshot_date : null
      const filters = Object.assign({}, { advancedFilters, url_id: urlId, snapshot_date, webPropertyGroupId: this.filters.webPropertyGroupId })
      this.getKeywordsByUrlIdData({ clientId: this.clientId, filters, screen: this.KEYWORDS_PATH.URL })
    },
    /**
     * callback for changing sort config
     * @param sortConfig
     */
    onSortChanged (sortConfig) {
      this.sortConfig = _.cloneDeep(sortConfig)
    },
    /**
     * helper method for calculating filtered sum
     * Used in {@link #_filteredSum}
     * @param data
     * @param acc
     */
    calculateFilteredSum (data, acc) {
      data.forEach(item => {
        this.sumConfig.cells.forEach(conf => {
          acc[conf.propName] += item[conf.propName]
        })
      })
    }
  },
  filters: {
    ...filters
  }
}
</script>

<style scoped lang="scss">
  .url {
    margin-bottom: -50px;

    &__row {
      display: flex;
      align-items: stretch;
      justify-content: space-between;
      margin-bottom: 30px;

      &-item {
        flex: 1 1 100%;
        overflow: hidden;

        &:first-child {
          margin-right: 14px;
        }
      }
    }

    &__web-property-select {
      width: 250px;
    }

    &__competitor{
      background-color: $rfx-red;
      width: 7px;
      height: 7px;
      border-radius: 50%;
      margin-right: 5px;
    }

    &__sibling{
      @extend .url__competitor;
      background-color: $rfx-medium-orange;
    }

    &__client{
      @extend .url__competitor;
      background-color: $rfx-light-green;
    }

    &__table {
      flex: 1 1;
    }

    &__chart-wrapper {
      width: 100%;
      padding: 15px 0 30px 40px;
      box-sizing: border-box;
    }

    &__table-controls {
      display: flex;
      align-items: stretch;
      justify-content: space-between;
    }

    &__radio-wrap {
      display: flex;
      align-items: center;
    }

    &__radio {
      margin-right: 30px;
    }

    &__select-wrap {
      display: flex;
      align-items: center;
    }

    &__select-holder {
      margin-left: 30px;
      position: relative;
      display: flex;

      &_main::after {
        background-color: $rfx-bright-blue;
      }

      &_comparing::after {
        background-color: $rfx-light-green;
      }
    }

    &__select {
      width: 160px;
    }

    &__count {
      color: #b2b9be;
      font-size: 14px;
      font-weight: 600;
      text-decoration: none;
      width: 75px;
      white-space: nowrap;
      margin-right: 30px;
    }

    &__pagination-props-select {
      display: flex;
      align-items: center;
      margin-right: 30px;
    }

    &__pagination {
      height: 60px;
    }

    &__advanced {
      margin-top: 30px;
      margin-bottom: 30px;
    }
  }
</style>
